import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, Lock, Pin, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  checkForumAdmin,
  listForumReports,
  moderateForumComment,
  moderateForumPost,
  resolveForumReport,
} from "@/lib/forum.functions";

export const Route = createFileRoute("/admin/forum")({
  head: () => ({
    meta: [{ title: "Admin · Forum" }, { name: "robots", content: "noindex" }],
  }),
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { isAdmin } = await checkForumAdmin();
    if (!isAdmin) throw redirect({ to: "/" });
  },
  component: AdminForum,
});

function AdminForum() {
  const qc = useQueryClient();
  const list = useServerFn(listForumReports);
  const resolve = useServerFn(resolveForumReport);
  const modPost = useServerFn(moderateForumPost);
  const modComment = useServerFn(moderateForumComment);
  const [showResolved, setShowResolved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "forum-reports", showResolved],
    queryFn: () => list({ data: { resolved: showResolved } }),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "forum-reports"] });
    qc.invalidateQueries({ queryKey: ["forum"] });
  }

  async function run(action: () => Promise<unknown>, ok: string) {
    try {
      await action();
      refresh();
      toast.success(ok);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    }
  }

  const reports = data?.reports ?? [];

  return (
    <section className="bg-warm">
      <div className="container-edge py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Admin · Moderering</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl">Rapporterat i forumet</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowResolved(false)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                !showResolved
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-border bg-card hover:bg-secondary"
              }`}
            >
              Öppna
            </button>
            <button
              onClick={() => setShowResolved(true)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                showResolved
                  ? "border-transparent bg-primary text-primary-foreground"
                  : "border-border bg-card hover:bg-secondary"
              }`}
            >
              Hanterade
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {isLoading && <p className="text-sm text-muted-foreground">Laddar rapporter…</p>}
          {!isLoading && !reports.length && (
            <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
              {showResolved ? "Inga hanterade rapporter." : "Inga öppna rapporter — allt är lugnt."}
            </div>
          )}

          {reports.map((r) => (
            <article key={r.id} className="rounded-2xl border border-border bg-card p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {new Date(r.created_at).toLocaleString("sv-SE")} · {r.post ? "Inlägg" : "Svar"}
              </p>
              <p className="mt-2 font-display text-lg">Orsak: {r.reason}</p>
              {r.details && <p className="mt-1 text-sm text-muted-foreground">{r.details}</p>}

              {r.post && (
                <p className="mt-3 text-sm">
                  <Link
                    to="/forum/t/$slug"
                    params={{ slug: r.post.slug }}
                    className="text-accent hover:underline"
                  >
                    {r.post.title}
                  </Link>
                </p>
              )}
              {r.comment && (
                <p className="mt-3 whitespace-pre-wrap break-words rounded-xl bg-muted/40 p-3 text-sm">
                  {r.comment.body.slice(0, 500)}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2 text-sm">
                {r.post && (
                  <>
                    <button
                      onClick={() =>
                        run(
                          () => modPost({ data: { id: r.post!.id, pinned: !r.post!.pinned } }),
                          r.post!.pinned ? "Avnålad" : "Fastnålad",
                        )
                      }
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:bg-secondary"
                    >
                      <Pin size={13} /> {r.post.pinned ? "Avnåla" : "Fastnåla"}
                    </button>
                    <button
                      onClick={() =>
                        run(
                          () => modPost({ data: { id: r.post!.id, locked: !r.post!.locked } }),
                          r.post!.locked ? "Upplåst" : "Låst",
                        )
                      }
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:bg-secondary"
                    >
                      <Lock size={13} /> {r.post.locked ? "Lås upp" : "Lås tråd"}
                    </button>
                    <button
                      onClick={() => {
                        if (!confirm("Ta bort tråden permanent?")) return;
                        run(
                          () => modPost({ data: { id: r.post!.id, remove: true } }),
                          "Tråden borttagen",
                        );
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-destructive/40 px-3 py-1.5 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 size={13} /> Ta bort tråd
                    </button>
                  </>
                )}
                {r.comment && (
                  <button
                    onClick={() => {
                      if (!confirm("Ta bort svaret?")) return;
                      run(
                        () => modComment({ data: { id: r.comment!.id } }),
                        "Svaret borttaget",
                      );
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-destructive/40 px-3 py-1.5 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 size={13} /> Ta bort svar
                  </button>
                )}
                <button
                  onClick={() =>
                    run(
                      () => resolve({ data: { id: r.id, resolved: !r.resolved } }),
                      r.resolved ? "Återöppnad" : "Markerad som hanterad",
                    )
                  }
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-primary-foreground"
                >
                  <Check size={13} /> {r.resolved ? "Återöppna" : "Klarmarkera"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
